import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import JsBarcode from "jsbarcode";
import Modal from "@/components/Modal";
import { money } from "@/lib/api";
import { Printer, Barcode as BarcodeIcon } from "lucide-react";

// Build one label per barcode: base unit + each configured packaging (generic, no hard-coded types).
function productLabels(p) {
  const out = [{ code: p.barcode, name: p.name, cond: `À l'unité (${p.base_unit})`, price: p.price }];
  for (const pk of p.packagings || []) {
    if (!pk.barcode) continue;
    out.push({ code: pk.barcode, name: p.name, cond: `${pk.name} = ${pk.factor} ${p.base_unit}`, price: pk.price ?? pk.factor * p.price });
  }
  return out.filter((l) => l.code);
}

function svgFor(code) {  // eslint-disable-line no-unused-vars
  const el = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  try { JsBarcode(el, String(code), { format: "CODE128", width: 1.6, height: 44, fontSize: 12, margin: 4, displayValue: true }); } catch (e) { /* ignore */ }
  return el.outerHTML;
}

export default function BarcodePrintSheet({ open, onClose, products, cats, orgName }) {
  const navigate = useNavigate();
  const [catId, setCatId] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});

  const filtered = useMemo(() => (products || []).filter((p) =>
    (!catId || p.category_id === catId) &&
    (!q.trim() || p.name.toLowerCase().includes(q.toLowerCase()))), [products, catId, q]);

  const toggle = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));
  const allVisible = filtered.length > 0 && filtered.every((p) => sel[p.id]);
  const toggleAll = () => { const n = {}; if (!allVisible) filtered.forEach((p) => { n[p.id] = true; }); setSel(n); };

  const chosen = (products || []).filter((p) => sel[p.id]);
  const labels = chosen.flatMap(productLabels);

  const print = () => {
    if (labels.length === 0) return;
    // Hand the selected labels to an internal same-domain print page (Chrome Android friendly).
    const data = labels.map((l) => ({ code: l.code, name: l.name, cond: l.cond, priceText: money(l.price) }));
    sessionStorage.setItem("bc_labels", JSON.stringify(data));
    onClose();
    navigate("/print/barcodes");
  };

  return (
    <Modal open={open} onClose={onClose} title="Imprimer les codes-barres" wide testId="barcode-sheet-modal"
      footer={<>
        <button className="btn-outline" onClick={onClose}>Fermer</button>
        <button className="btn-primary" onClick={print} disabled={labels.length === 0} data-testid="barcode-sheet-print-btn">
          <Printer className="w-4 h-4" /> Imprimer ({labels.length})
        </button>
      </>}>
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <select className="inp !w-auto" value={catId} onChange={(e) => setCatId(e.target.value)} data-testid="barcode-sheet-cat">
            <option value="">Toutes les catégories</option>
            {(cats || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input className="inp flex-1 min-w-[160px]" placeholder="Rechercher un produit…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="barcode-sheet-search" />
          <button className="btn-outline" onClick={toggleAll} data-testid="barcode-sheet-selectall">{allVisible ? "Tout décocher" : "Tout cocher"}</button>
        </div>
        <div className="border border-[#E2E8F0] rounded-lg max-h-72 overflow-y-auto divide-y divide-[#F1F5F9]">
          {filtered.length === 0 && <div className="p-4 text-sm text-[#94A3B8] text-center">Aucun produit</div>}
          {filtered.map((p) => (
            <label key={p.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#F8FAFC]" data-testid={`barcode-sheet-row-${p.id}`}>
              <input type="checkbox" checked={!!sel[p.id]} onChange={() => toggle(p.id)} />
              <span className="flex-1 text-sm font-medium text-[#0F172A]">{p.name}</span>
              <span className="text-xs text-[#64748B] inline-flex items-center gap-1"><BarcodeIcon className="w-3.5 h-3.5" />{1 + (p.packagings?.filter((k) => k.barcode).length || 0)} étiquette(s)</span>
            </label>
          ))}
        </div>
        <p className="text-xs text-[#64748B]">Une feuille A4 compacte est générée : une étiquette par conditionnement (unité + lots), avec produit, conditionnement, prix, code-barres et sa valeur lisible. À garder près de la caisse.</p>
      </div>
    </Modal>
  );
}
